import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import apiService from '../../../services/api.service';
import './GalleryForm.css';

const GalleryForm = () => {
  const navigate = useNavigate();
  const { id } = useParams();
  const isEditMode = !!id;

  const [formData, setFormData] = useState({
    category_title: '',
    category_location: '',
    category_order: '0',
    category_image: '',
    display_status: '1'
  });

  const [errors, setErrors] = useState({});
  const [loading, setLoading] = useState(false);
  const [fetchLoading, setFetchLoading] = useState(false);
  const [error, setError] = useState(null);
  const [successMessage, setSuccessMessage] = useState('');

  useEffect(() => {
    if (isEditMode) {
      const fetchGallery = async () => {
        try {
          setFetchLoading(true);
          setError(null);
          const response = await apiService.getAdminGalleryById(id);
          if (response.data.success) {
            const gallery = response.data.data;
            setFormData({
              category_title: gallery.category_title || '',
              category_location: gallery.category_location || '',
              category_order: gallery.category_order || '0',
              category_image: gallery.category_image || '',
              display_status: gallery.display_status || '1'
            });
          } else {
            setError('Failed to load gallery');
          }
        } catch (err) {
          setError(err.response?.data?.error || 'Failed to load gallery');
          console.error('Gallery fetch error:', err);
        } finally {
          setFetchLoading(false);
        }
      };
      fetchGallery();
    }
  }, [id, isEditMode]);

  const validateForm = () => {
    const newErrors = {};

    if (!formData.category_title.trim()) {
      newErrors.category_title = 'Category title is required';
    } else if (formData.category_title.trim().length < 3) {
      newErrors.category_title = 'Title must be at least 3 characters';
    }

    if (!formData.category_location.trim()) {
      newErrors.category_location = 'Location is required';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
    // Clear error for this field
    if (errors[name]) {
      setErrors(prev => ({
        ...prev,
        [name]: ''
      }));
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!validateForm()) {
      return;
    }

    setLoading(true);
    setError(null);
    setSuccessMessage('');

    try {
      const response = isEditMode
        ? await apiService.updateGallery(id, formData)
        : await apiService.createGallery(formData);

      if (response.data.success) {
        setSuccessMessage(isEditMode ? 'Gallery updated successfully!' : 'Gallery created successfully!');
        setTimeout(() => {
          navigate('/admin/galleries');
        }, 1500);
      } else {
        setError(response.data.error || 'Failed to save gallery');
      }
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to save gallery');
      console.error('Gallery save error:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleCancel = () => {
    navigate('/admin/galleries');
  };

  if (fetchLoading) {
    return (
      <div className="gallery-form-container">
        <div className="loading">Loading gallery...</div>
      </div>
    );
  }

  return (
    <div className="gallery-form-container">
      <div className="gallery-form-header">
        <h2>{isEditMode ? 'Edit Gallery Category' : 'Create New Gallery Category'}</h2>
      </div>

      {error && (
        <div className="alert alert-error">
          <strong>Error:</strong> {error}
        </div>
      )}

      {successMessage && (
        <div className="alert alert-success">
          <strong>Success:</strong> {successMessage}
        </div>
      )}

      <form onSubmit={handleSubmit} className="gallery-form">
        <div className="form-group">
          <label htmlFor="category_title">Category Title *</label>
          <input
            type="text"
            id="category_title"
            name="category_title"
            value={formData.category_title}
            onChange={handleChange}
            className={errors.category_title ? 'error' : ''}
            disabled={loading}
            placeholder="Enter gallery category title"
          />
          {errors.category_title && (
            <span className="error-message">{errors.category_title}</span>
          )}
        </div>

        <div className="form-group">
          <label htmlFor="category_location">Location *</label>
          <input
            type="text"
            id="category_location"
            name="category_location"
            value={formData.category_location}
            onChange={handleChange}
            className={errors.category_location ? 'error' : ''}
            disabled={loading}
            placeholder="Enter location (e.g., Mumbai, India)"
          />
          {errors.category_location && (
            <span className="error-message">{errors.category_location}</span>
          )}
        </div>

        <div className="form-group">
          <label htmlFor="category_image">Category Image URL</label>
          <input
            type="text"
            id="category_image"
            name="category_image"
            value={formData.category_image}
            onChange={handleChange}
            disabled={loading}
            placeholder="Enter image URL (optional)"
          />
          <small className="form-text">
            Provide a URL to an image that represents this gallery category
          </small>
        </div>

        <div className="form-row">
          <div className="form-group">
            <label htmlFor="category_order">Display Order</label>
            <input
              type="number"
              id="category_order"
              name="category_order"
              value={formData.category_order}
              onChange={handleChange}
              disabled={loading}
              min="0"
            />
            <small className="form-text">
              Lower numbers appear first
            </small>
          </div>

          <div className="form-group">
            <label htmlFor="display_status">Status</label>
            <select
              id="display_status"
              name="display_status"
              value={formData.display_status}
              onChange={handleChange}
              disabled={loading}
            >
              <option value="1">Active</option>
              <option value="0">Inactive</option>
            </select>
          </div>
        </div>

        <div className="form-actions">
          <button
            type="button"
            className="btn btn-cancel"
            onClick={handleCancel}
            disabled={loading}
          >
            Cancel
          </button>
          <button
            type="submit"
            className="btn btn-primary"
            disabled={loading}
          >
            {loading ? 'Saving...' : (isEditMode ? 'Update Gallery' : 'Create Gallery')}
          </button>
        </div>
      </form>
    </div>
  );
};

export default GalleryForm;
